import type {
  CreateLabOrderRequest,
  LabOrderErrorCode,
  LabOrderResponse,
  LabTest,
  LabTestResponse,
  LabTestsResponse,
} from './bloodTests.types';

/** A failed catalog/order call, carrying the BFF error code (never patient data). */
export class LabOrderError extends Error {
  readonly code: LabOrderErrorCode;

  constructor(code: LabOrderErrorCode) {
    super(code);
    this.name = 'LabOrderError';
    this.code = code;
  }
}

const KNOWN_FORBIDDEN_CODES = new Set<LabOrderErrorCode>([
  'lab_ordering_unavailable',
  'laeknanumer_required',
  'impersonation_not_allowed',
]);

const readErrorCode = async (res: Response): Promise<LabOrderErrorCode> => {
  if (res.status === 422) return 'invalid_request';
  if (res.status === 409) return 'lab_order_in_progress';
  if (res.status === 403) {
    const body: unknown = await res.json().catch(() => null);
    const code = (body as { error?: unknown } | null)?.error;
    if (typeof code === 'string' && KNOWN_FORBIDDEN_CODES.has(code as LabOrderErrorCode)) {
      return code as LabOrderErrorCode;
    }
    return 'lab_ordering_unavailable';
  }
  return 'lab_order_failed';
};

export const mapLabTest = (test: LabTestResponse): LabTest => ({
  code: test.code,
  name: test.name,
  displayName: test.display_name ?? undefined,
  price: test.price ?? undefined,
  group: test.group ?? undefined,
  includedTestCodes: test.included_test_codes ?? [],
  recommendedTestCodes: test.recommended_test_codes ?? [],
});

export const fetchLabTests = async (): Promise<LabTest[]> => {
  const res = await fetch('/api/labs/tests', { credentials: 'include' });
  if (!res.ok) throw new LabOrderError(await readErrorCode(res));
  const body = (await res.json()) as LabTestsResponse;
  return body.tests.map(mapLabTest);
};

/** Resolves for succeeded/partial_success (201) and unknown (202); the BFF sends a failed batch as 502, which rejects. */
export const createLabOrder = async (request: CreateLabOrderRequest): Promise<LabOrderResponse> => {
  const res = await fetch('/api/labs/orders', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!res.ok) throw new LabOrderError(await readErrorCode(res));
  return (await res.json()) as LabOrderResponse;
};
